// src/components/EditGoodModal.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { getApiErrorLines } from "../utils/formErrors";
import { formatErrorLines } from "../utils/toastErrors";
import {
  BadgePercent,
  Calculator,
  Hash,
  Layers3,
  Package,
  Save,
  X,
} from "lucide-react";

const cx = (...classes) => classes.filter(Boolean).join(" ");

const toNumOrZero = (v) => {
  if (v === "" || v === null || v === undefined) return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const Field = ({
  id,
  label,
  value,
  onChange,
  type = "text",
  required,
  icon: Icon,
  inputMode,
  placeholder,
}) => (
  <div className="space-y-1.5">
    <label
      htmlFor={id}
      className="block text-sm font-medium text-slate-700 text-right"
    >
      {label}
      {required ? <span className="text-rose-500"> *</span> : null}
    </label>

    <div className="relative">
      {Icon ? (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
          <Icon size={18} />
        </span>
      ) : null}

      <input
        id={id}
        type={type}
        inputMode={inputMode}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        required={required}
        className={cx(
          "w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-right text-slate-900",
          "placeholder:text-slate-400 shadow-sm",
          "focus:outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary",
          Icon ? "pr-10" : "pr-3"
        )}
      />
    </div>
  </div>
);

const TextArea = ({ id, label, value, onChange, required, icon: Icon }) => (
  <div className="space-y-1.5">
    <label
      htmlFor={id}
      className="block text-sm font-medium text-slate-700 text-right"
    >
      {label}
      {required ? <span className="text-rose-500"> *</span> : null}
    </label>

    <div className="relative">
      {Icon ? (
        <span className="absolute right-3 top-3 text-slate-400 pointer-events-none">
          <Icon size={18} />
        </span>
      ) : null}

      <textarea
        id={id}
        value={value}
        onChange={onChange}
        required={required}
        rows={4}
        className={cx(
          "w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-right text-slate-900",
          "placeholder:text-slate-400 shadow-sm resize-y",
          "focus:outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary",
          Icon ? "pr-10" : "pr-3"
        )}
        placeholder="شرح کالا را وارد کنید"
      />
    </div>
  </div>
);

const EditGoodModal = ({ good, onClose, onSave }) => {
  const dialogRef = useRef(null);
  const firstInputRef = useRef(null);

  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [customsValue, setCustomsValue] = useState("");
  const [importRights, setImportRights] = useState("");
  const [discount, setDiscount] = useState("");
  const [totalValue, setTotalValue] = useState("");
  const [addedValue, setAddedValue] = useState("");
  const [redCersent, setRedCersent] = useState("");
  const [otherExpense, setOtherExpense] = useState("");
  const [quantity, setQuantity] = useState("");
  const [HSCode, setHSCode] = useState("");
  const [goodsDescription, setGoodsDescription] = useState("");

  useEffect(() => {
    if (!good) return;

    setCustomsValue(good.customs_value ?? "");
    setImportRights(good.import_rights ?? "");
    setDiscount(good.discount ?? "");
    setTotalValue(good.total_value ?? "");
    setAddedValue(good.added_value ?? "");
    setRedCersent(good.red_cersent ?? "");
    setOtherExpense(good.other_expense ?? "");
    setQuantity(good.quantity ?? "");
    setHSCode(good.HSCode ?? "");
    setGoodsDescription(good.goods_description ?? "");
    setErrorMsg("");
    setSaving(false);

    // focus
    setTimeout(() => firstInputRef.current?.focus?.(), 0);
  }, [good]);

  const canSubmit = useMemo(() => {
    // Required numeric fields + description
    return (
      goodsDescription?.trim?.() &&
      String(quantity) !== "" &&
      String(customsValue) !== "" &&
      String(importRights) !== "" &&
      String(totalValue) !== "" &&
      String(addedValue) !== "" &&
      String(otherExpense) !== "" &&
      String(redCersent) !== ""
    );
  }, [
    goodsDescription,
    quantity,
    customsValue,
    importRights,
    totalValue,
    addedValue,
    otherExpense,
    redCersent,
  ]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");

    if (!good) {
      setErrorMsg("کالای انتخاب‌شده معتبر نیست.");
      return;
    }

    setSaving(true);
    try {
      const updatedGood = {
        ...good,
        customs_value: toNumOrZero(customsValue),
        import_rights: toNumOrZero(importRights),
        discount: toNumOrZero(discount),
        total_value: toNumOrZero(totalValue),
        added_value: toNumOrZero(addedValue),
        red_cersent: toNumOrZero(redCersent),
        other_expense: toNumOrZero(otherExpense),
        quantity: Math.max(0, parseInt(quantity, 10) || 0),
        HSCode,
        goods_description: goodsDescription,
      };

      await onSave?.(updatedGood);
    } catch (err) {
      // A JS error's message is English; only API errors carry a reason to show.
      const msg = err?.response || err?.request
        ? formatErrorLines(getApiErrorLines(err, {}, "خطا در ذخیره تغییرات"))
        : "خطا در ذخیره تغییرات";
      setErrorMsg(msg);
      toast.error(msg);
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 px-3 sm:px-6 font-vazirmatn"
      onMouseDown={handleBackdropClick}
      dir="rtl"
      role="dialog"
      aria-modal="true"
    >
      <div
        ref={dialogRef}
        className="w-full max-w-3xl rounded-2xl border border-slate-200 bg-white shadow-xl overflow-hidden"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 sm:px-7 py-5 border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white">
          <div className="flex flex-row-reverse items-center justify-between gap-3">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 transition"
            >
              <X size={18} />
              بستن
            </button>

            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
                <Layers3 size={22} />
              </div>
              <div className="text-right">
                <h2 className="text-lg sm:text-xl font-extrabold text-slate-800">
                  ویرایش کالا
                </h2>
                <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
                  مقادیر مالی و شرح کالا را اصلاح کنید.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="px-5 sm:px-7 py-6">
          {errorMsg ? (
            <div className="mb-5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-right text-rose-700 text-sm">
              {errorMsg}
            </div>
          ) : null}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <TextArea
                id="goods_description"
                label="شرح کالا"
                value={goodsDescription}
                onChange={(e) => setGoodsDescription(e.target.value)}
                required
                icon={Package}
              />
            </div>

            <Field
              id="HSCode"
              label="HS Code"
              inputMode="numeric"
              placeholder="مثلا 73041990"
              value={HSCode}
              onChange={(e) => setHSCode(e.target.value)}
              icon={Hash}
            />

            <Field
              id="customs_value"
              label="ارزش گمرکی"
              type="number"
              inputMode="numeric"
              placeholder="مثلاً 1000000"
              value={customsValue}
              onChange={(e) => setCustomsValue(e.target.value)}
              required
              icon={Calculator}
            />

            <Field
              id="import_rights"
              label="حقوق ورودی"
              type="number"
              inputMode="numeric"
              placeholder="مثلاً 250000"
              value={importRights}
              onChange={(e) => setImportRights(e.target.value)}
              required
              icon={Calculator}
              // focus first numeric
              ref={undefined}
            />

            <Field
              id="discount"
              label="تخفیف"
              type="number"
              inputMode="numeric"
              placeholder="مثلاً 0"
              value={discount}
              onChange={(e) => setDiscount(e.target.value)}
              icon={BadgePercent}
            />

            <Field
              id="total_value"
              label="ارزش کل ارزی"
              type="number"
              inputMode="numeric"
              placeholder="مثلاً 5000000"
              value={totalValue}
              onChange={(e) => setTotalValue(e.target.value)}
              required
              icon={Calculator}
            />

            <Field
              id="added_value"
              label="ارزش افزوده"
              type="number"
              inputMode="numeric"
              placeholder="مثلاً 400000"
              value={addedValue}
              onChange={(e) => setAddedValue(e.target.value)}
              required
              icon={Calculator}
            />

            <Field
              id="other_expense"
              label="سایر هزینه‌ها"
              type="number"
              inputMode="numeric"
              placeholder="مثلاً 150000"
              value={otherExpense}
              onChange={(e) => setOtherExpense(e.target.value)}
              required
              icon={Calculator}
            />

            <Field
              id="red_cersent"
              label="حلال احمر"
              type="number"
              inputMode="numeric"
              placeholder="مثلاً 0"
              value={redCersent}
              onChange={(e) => setRedCersent(e.target.value)}
              required
              icon={Calculator}
            />

            <div className="md:col-span-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-slate-700 text-right">
                    تعداد <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
                      <Hash size={18} />
                    </span>
                    <input
                      ref={firstInputRef}
                      id="quantity"
                      type="number"
                      inputMode="numeric"
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                      required
                      className={cx(
                        "w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 pr-10 text-right text-slate-900",
                        "placeholder:text-slate-400 shadow-sm",
                        "focus:outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary"
                      )}
                      placeholder="مثلاً 10"
                    />
                  </div>
                </div>

                {/* Derived preview */}
                <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
                  <div className="text-right">
                    <div className="text-sm font-extrabold text-slate-800">
                      پیش‌نمایش
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      این بخش صرفاً جهت بررسی سریع است.
                    </div>

                    <div className="mt-3 space-y-2 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-slate-600">تعداد</span>
                        <span className="font-bold text-slate-800">
                          {Number(quantity || 0).toLocaleString("fa-IR")}
                        </span>
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-slate-600">ارزش کل ارزی</span>
                        <span className="font-bold text-slate-800">
                          {toNumOrZero(totalValue).toLocaleString("fa-IR")}
                        </span>
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-slate-600">حقوق ورودی</span>
                        <span className="font-bold text-slate-800">
                          {toNumOrZero(importRights).toLocaleString("fa-IR")}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between border-t border-slate-200 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50 transition"
              disabled={saving}
            >
              انصراف
            </button>

            <button
              type="submit"
              disabled={!canSubmit || saving}
              className={cx(
                "inline-flex items-center justify-center gap-2 rounded-xl px-5 py-2.5 text-sm font-extrabold text-white",
                "bg-primary hover:opacity-95 active:opacity-90 transition shadow-sm",
                "disabled:opacity-50 disabled:cursor-not-allowed"
              )}
            >
              <Save size={18} />
              {saving ? "در حال ذخیره..." : "ذخیره تغییرات"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditGoodModal;
